/**
 * TX-015 hybrid input. A short press that barely moves is a tap (walk there); moving past TAP_PX turns the press into a
 * floating stick anchored where the finger went down. The stick only steers through ordinary `move`/`stop` commands, so
 * every peer simulates the same thing.
 */
export const TAP_PX = 12;
/** Alpha tuning: generous for small hands (spec suggests 180 ms; tune with family tests). */
export const TAP_MS = 250;
/** How far ahead of the captain (world units) the stick aims, and how often it may re-aim. */
export const STICK_REACH = 140;
export const STICK_RESEND_MS = 150;
/** Magnetic attraction radius for joystick touch steering near work desks. */
export const STICK_MAGNET_RADIUS = 90;

export type GestureResult = { kind: 'none' } | { kind: 'tap'; x: number; y: number } | { kind: 'steer'; dx: number; dy: number } | { kind: 'release' };

export class Gesture {
    private start: { x: number; y: number; t: number } | null = null;
    private steering = false;
    private lastSteer = -Infinity;
    get active() { return this.start !== null; }
    get stick() { return this.steering && this.start ? { x: this.start.x, y: this.start.y } : null; }
    down(x: number, y: number, t: number) { this.start = { x, y, t }; this.steering = false; this.lastSteer = -Infinity; }
    /** Screen-space drag from the anchor; returns a unit steer direction at most every STICK_RESEND_MS. */
    move(x: number, y: number, t: number): GestureResult {
        if (!this.start) return { kind: 'none' };
        const dx = x - this.start.x, dy = y - this.start.y, length = Math.hypot(dx, dy);
        if (!this.steering && length < TAP_PX) return { kind: 'none' };
        this.steering = true;
        if (t - this.lastSteer < STICK_RESEND_MS || length < 1) return { kind: 'none' };
        this.lastSteer = t;
        return { kind: 'steer', dx: dx / length, dy: dy / length };
    }
    up(x: number, y: number, t: number): GestureResult {
        const start = this.start;
        this.start = null;
        if (!start) return { kind: 'none' };
        if (this.steering) { this.steering = false; return { kind: 'release' }; }
        if (t - start.t <= TAP_MS && Math.hypot(x - start.x, y - start.y) < TAP_PX) return { kind: 'tap', x, y };
        return { kind: 'none' };
    }
    cancel(): GestureResult { const was = this.steering; this.start = null; this.steering = false; return was ? { kind: 'release' } : { kind: 'none' }; }
}
